<template>
  <n-config-provider :locale="naiveLocale" :theme="theme" :theme-overrides="themeOverrides">
    <n-dialog-provider>
      <LicenseWarningBanner />
      <router-view />
      <UpdateToast />
      <IntercomKeyboardHelp v-model:show="showKeyboardHelp" />

      <Transition name="g-panel-fade">
        <div v-if="pendingG" class="g-panel-overlay" @click="cancelPendingG">
          <div class="g-panel" @click.stop>
            <div class="g-panel-header">
              <kbd class="g-panel-kbd">G</kbd>
              <span class="g-panel-title">Go to...</span>
            </div>
            <div class="g-panel-grid">
              <div
                v-for="item in gRouteItems"
                :key="item.key"
                class="g-panel-item"
              >
                <kbd class="g-panel-key">{{ item.key.toUpperCase() }}</kbd>
                <span class="g-panel-label">{{ item.label }}</span>
              </div>
            </div>
            <div class="g-panel-footer">
              Press a key or <kbd class="g-panel-kbd-sm">Esc</kbd> to cancel
            </div>
          </div>
        </div>
      </Transition>
    </n-dialog-provider>
  </n-config-provider>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { NConfigProvider, NDialogProvider, enUS, zhCN } from 'naive-ui'
import LicenseWarningBanner from '@shared/components/LicenseWarningBanner.vue'
import UpdateToast from '@shared/components/UpdateToast.vue'
import IntercomKeyboardHelp from '@shared/components/intercom/IntercomKeyboardHelp.vue'
import { useGlobalShortcuts } from '@shared/composables/useGlobalShortcuts.js'

const { locale } = useI18n()
const router = useRouter()

// Global keyboard shortcuts (G-sequences for page navigation)
const showKeyboardHelp = ref(false)

// Route map with labels for the G-panel
const gRouteMap = {
  d: { path: '/', label: 'Dashboard' },
  b: { path: '/books', label: 'Books' },
  m: { path: '/members', label: 'Members' },
  c: { path: '/circulation', label: 'Circulation' },
  r: { path: '/reservations', label: 'Reservations' },
  f: { path: '/fines', label: 'Fines' },
  n: { path: '/no-dues', label: 'No-Dues' },
  u: { path: '/subjects', label: 'Subjects' },
  a: { path: '/naac', label: 'NAAC Dashboard' },
  e: { path: '/accession-register', label: 'Accession Register' },
  v: { path: '/stock-verification', label: 'Stock Verification' },
  t: { path: '/footfall', label: 'Footfall' },
  p: { path: '/reports', label: 'Reports' },
  s: { path: '/settings', label: 'Settings' },
}

// Flat route map for the composable (key -> path)
const routes = Object.fromEntries(
  Object.entries(gRouteMap).map(([k, v]) => [k, v.path])
)

// Items for the G-panel display
const gRouteItems = Object.entries(gRouteMap).map(([key, { label }]) => ({ key, label }))

const { pendingG, cancelG } = useGlobalShortcuts(router, {
  routes,
  onHelp: () => { showKeyboardHelp.value = !showKeyboardHelp.value },
})

const cancelPendingG = () => cancelG?.()

// Naive UI locale (map from i18n locale)
const naiveLocale = computed(() => {
  return locale.value === 'ta_IN' ? zhCN : enUS
})

// Theme overrides to align naive-ui dialogs with Intercom design language
const themeOverrides = {
  common: {
    primaryColor: '#0057FF',
    primaryColorHover: '#0048D4',
    primaryColorPressed: '#003CB3',
    errorColor: '#dc2626',
    errorColorHover: '#b91c1c',
    errorColorPressed: '#991b1b',
    borderRadius: '8px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  Dialog: {
    borderRadius: '12px',
    padding: '24px',
    titleFontSize: '16px',
    fontSize: '14px',
    iconSize: '24px',
  },
  Button: {
    borderRadiusMedium: '8px',
    fontSizeMedium: '14px',
    heightMedium: '36px',
  },
}

// Optional: theme configuration
const theme = ref(null) // null = light theme
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: #f5f5f5;
}

#app {
  min-height: 100vh;
}

/* G-panel (which-key style navigation popup -- Intercom design) */
.g-panel-overlay {
  position: fixed;
  inset: 0;
  z-index: 99998;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 40px;
  background: rgba(0, 0, 0, 0.08);
}

.g-panel {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #EBEBEB;
  padding: 16px 20px;
  min-width: 440px;
  max-width: 560px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
}

.g-panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #EBEBEB;
}

.g-panel-kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: #1F1F1F;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  font-family: -apple-system, BlinkMacSystemFont, monospace;
  color: #fff;
}

.g-panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.g-panel-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2px 20px;
}

.g-panel-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: default;
  transition: background 0.1s;
}

.g-panel-item:hover {
  background: #F5F5F5;
}

.g-panel-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: #E6F0FF;
  border: 1px solid #99BFFF;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  font-family: -apple-system, BlinkMacSystemFont, monospace;
  color: #0057FF;
}

.g-panel-label {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
}

.g-panel-footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #EBEBEB;
  font-size: 11px;
  color: #334155;
  text-align: center;
}

.g-panel-kbd-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 5px;
  background: #F5F5F5;
  border: 1px solid #D1D1D1;
  border-radius: 3px;
  font-size: 10px;
  font-family: -apple-system, BlinkMacSystemFont, monospace;
  color: #334155;
}

.g-panel-fade-enter-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}
.g-panel-fade-leave-active {
  transition: opacity 0.08s ease, transform 0.08s ease;
}
.g-panel-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.g-panel-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
